import type {
  Font,
  FontWeightOption,
  TemplateFont,
  TemplateFontMappingType,
} from 'shared/interfaces/planogram';

enum FontWeightMap {
  thin = '100',
  hairline = '100',
  'extra light' = '200',
  'ultra light' = '200',
  light = '300',
  book = '300',
  regular = '400',
  normal = '400',
  medium = '500',
  'semi bold' = '600',
  bold = '700',
  'extra bold' = '800',
  black = '900',
}

const possibleFontExtensions = ['ttf', 'otf'];

const getFontFace = (font: Font) => {
  const fontFace = new FontFace(font.font_family, `url(${font.file_url})`, {});

  fontFace.weight = font.computedWeight ?? '400';

  return fontFace;
};

const replaceNonAlphanumericWithDash = (text: string) => text.replace(/[\W]/g, '-');

export const getDisplayFontFamilyName = (familyName: string) => {
  const splitFamilyName = familyName.split('.');
  const familyNameHasAppendedType = possibleFontExtensions.includes(
    splitFamilyName[splitFamilyName.length - 1],
  );

  if (familyNameHasAppendedType) {
    splitFamilyName.splice(-1);
  }

  return replaceNonAlphanumericWithDash(splitFamilyName.join('.'));
};

export const getSafeFontFamilyName = (familyName: string) =>
  `Font-${replaceNonAlphanumericWithDash(familyName)}`;

export const loadFontFacesToDocument = async (fonts: Font[]) => {
  const loadedFonts: Font[] = [];

  await Promise.all(
    fonts.map(async (font: Font) => {
      const fontFace = getFontFace(font);

      try {
        const loadedFont = await fontFace.load();
        document.fonts.add(loadedFont);
        loadedFonts.push(font);
      } catch (error) {
        console.error('Font not supported:', fontFace.family);
      }
    }),
  );

  return loadedFonts;
};

export const computeFontProperties = (font: Font) => {
  const fontWeight = font.font_weight !== null ? font.font_weight : 'Regular';
  const computedWeight = font.font_weight !== null ? computeFontWeight(font.font_weight) : '400';

  return {
    font_family: getSafeFontFamilyName(font.font_family ?? font.name),
    display_font_family: getDisplayFontFamilyName(font.font_family ?? font.name),
    computedWeight,
    font_weight: fontWeight,
  };
};

export const computeFontWeight = (weight: string) => {
  if (!weight) {
    return '400';
  }
  const weightKey = weight.toLowerCase() as keyof typeof FontWeightMap;

  return FontWeightMap[weightKey] ?? '400';
};
